import { createStore, applyMiddleware } from 'redux';
import { createLogger } from "redux-logger";
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'localforage';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import { thunk } from 'redux-thunk';
import reducers from '../reducers';
import {
  setInboxMicroservicePlatforms,
  setInboxMicroserviceMigratingPlatforms,
  syncInboxMicroserviceFlagsFromRetailerInfo
} from '../services/inbox/inboxMsChatPlatform';
import { normalizeFunnelSteps } from '../reducers/funnelStepsState';

const middlewares = [];

const logger = createLogger({
  level: "info",
  collapsed: false,
  logger: console,
  predicate: (_getState, _action) => true
});

if (process.env.NODE_ENV !== "production") {
  middlewares.push(logger);
}

/**
 * El chat abierto desde fuera de la lista (selector de canales del customer) es
 * estado de sesión: a diferencia de `data`, nada lo limpia al recargar la lista, así
 * que persistirlo revive una conversación vieja con atributos rancios después de un
 * reload. Se anula en ambos sentidos: al guardar, para no escribirlo más, y al
 * rehidratar, para neutralizar lo que ya haya en localforage de sesiones previas.
 */
const dropExternalConversation = createTransform(
  (inboundState) => ({ ...inboundState, externalConversation: null }),
  (outboundState) => ({ ...outboundState, externalConversation: null }),
  { whitelist: ['conversationsReducer'] }
);

const funnelStepsTransform = createTransform(
  (inboundState) => {
    if (!inboundState) return inboundState;
    return {
      ...inboundState,
      funnelSteps: normalizeFunnelSteps(inboundState.funnelSteps)
    };
  },
  (outboundState) => {
    if (!outboundState) return outboundState;
    return {
      ...outboundState,
      funnelSteps: normalizeFunnelSteps(outboundState.funnelSteps)
    };
  },
  { whitelist: ['mainReducer'] }
);

const persistConfig = {
  blacklist: ['navigation'],
  key: 'root',
  stateReconciler: autoMergeLevel2,
  timeout: null,
  storage,
  transforms: [dropExternalConversation, funnelStepsTransform]
};

const pReducer = persistReducer(persistConfig, reducers);

const store = createStore(pReducer, applyMiddleware(thunk, ...middlewares));

/**
 * `isInboxMsChatPlatform` resuelve contra una variable de módulo que solo poblaba
 * `/api/v1/retailer_info`. Como `retailerUsersReducer` sí se persiste, tras un reload
 * la lista se pinta —y se puede abrir un chat— antes de que ese fetch vuelva, y en esa
 * ventana el mapa está vacío: cada decisión de ruteo responde "monolito" y el `read_chat`
 * automático de la apertura se va a Rails. Rehidratar el mapa cierra la ventana.
 */
const unsetFlag = Symbol('inbox-ms-flags-unset');
let syncedPlatforms = unsetFlag;
let syncedMigrating = unsetFlag;

function syncInboxFlagsFromStore() {
  const retailerInfo = store.getState().retailerUsersReducer?.retailer_info;
  const platforms = retailerInfo?.inbox_microservice_platforms;
  const migrating = retailerInfo?.inbox_microservice_migrating_platforms;
  if (platforms === syncedPlatforms && migrating === syncedMigrating) return;
  syncedPlatforms = platforms;
  syncedMigrating = migrating;
  syncInboxMicroserviceFlagsFromRetailerInfo(retailerInfo);
}

// Cualquier `GET_RETAILER_INFO` (checkout, planes, integraciones) deja el hash en
// Redux. El módulo que usa el ruteo tiene que seguir ese hash, no el retailer anterior.
store.subscribe(syncInboxFlagsFromStore);
syncInboxFlagsFromStore();

const persistor = persistStore(store, null, () => {
  const { retailer_info: retailerInfo } = store.getState().retailerUsersReducer || {};
  if (retailerInfo?.inbox_microservice_platforms) {
    setInboxMicroservicePlatforms(retailerInfo.inbox_microservice_platforms);
  }
  if (retailerInfo?.inbox_microservice_migrating_platforms) {
    setInboxMicroserviceMigratingPlatforms(retailerInfo.inbox_microservice_migrating_platforms);
  }
  syncedPlatforms = retailerInfo?.inbox_microservice_platforms;
  syncedMigrating = retailerInfo?.inbox_microservice_migrating_platforms;
});

export { store, persistor };
